// src/Login.js
import React, { useState, useEffect, useCallback } from 'react';
import { saveUserSession } from './utils/cookies';
import { getTranslation } from './languages';
import { API_ENDPOINTS } from './config';
import { fetchWithRetry, RateLimitError } from './utils/rateLimitHandler';
import Register from './Register';
import ResetPassword from './ResetPassword';

export default function Login({ onLoginSuccess, uiLanguage = 'en' }) {
    // Check for password reset token immediately on component initialization
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('reset_token');
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');
    
    // Initialize with token from URL if present
    const [resetToken, setResetToken] = useState(tokenFromUrl);
    

    const t = useCallback((key) => getTranslation(key, uiLanguage), [uiLanguage]);

    // Update document title for login page
    useEffect(() => {
        document.title = getTranslation('appTitle', uiLanguage);
    }, [uiLanguage]);

    const handleGoogleSignIn = useCallback(async (response) => {
        setIsGoogleLoading(true);
        setError('');

        try {
            const googleResponse = await fetchWithRetry(
                async () => fetch(API_ENDPOINTS.GOOGLE_LOGIN, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken: response.credential }),
                }),
                {
                    onRetry: ({ attempt, delay, maxRetries }) => {
                        setError(getTranslation('retryingRequest', uiLanguage, { seconds: Math.round(delay/1000) }));
                    }
                }
            );

            const data = await googleResponse.json();

            if (googleResponse.ok && data.success) {
                // Save user session to cookies only if "Remember Me" is checked
                if (rememberMe && data.userId !== '1') {
                    saveUserSession(data.userId, data.username, data.sessionId);
                }
                // Store session token
                if (data.sessionId) {
                    localStorage.setItem('sessionId', data.sessionId);
                    localStorage.setItem('sessionExpiresAt', data.expiresAt);
                } else {
                    console.error('[Login] No sessionId in Google login response!');
                }
                onLoginSuccess(data.userId, {
                    name: data.name,
                    email: data.username,
                    picture: data.picture
                });
            } else {
                setError(data.error || t('googleLoginFailed'));
            }
        } catch (err) {
            console.error('[Login] Google login error:', err);
            if (err instanceof RateLimitError) {
                setError(t('rateLimitExceeded'));
            } else {
                setError(t('connectionError'));
            }
        } finally {
            setIsGoogleLoading(false);
        }
    }, [rememberMe, onLoginSuccess, t, uiLanguage]);

    // Initialize Google Identity Services
    useEffect(() => {
        const loadGoogleScript = () => {
            if (window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
                // Clean up any existing button first
                const buttonContainer = document.getElementById('google-signin-button');
                if (buttonContainer) {
                    buttonContainer.innerHTML = '';
                }

                try {
                    window.google.accounts.id.initialize({
                        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                        callback: handleGoogleSignIn,
                        auto_select: false,
                        cancel_on_tap_outside: true,
                        use_fedcm_for_prompt: false,
                    });

                    // Only render button if container exists
                    if (buttonContainer) {
                        window.google.accounts.id.renderButton(
                            buttonContainer,
                            { 
                                theme: 'outline', 
                                size: 'large',
                                text: 'signin_with'
                            }
                        );
                    }
                } catch (error) {
                    console.error('Google Sign-In initialization error:', error);
                }
            }
        };

        // Add a small delay to ensure DOM is ready
        const initTimeout = setTimeout(() => {
            // Load Google Identity Services script
            if (!window.google) {
                const script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                script.defer = true;
                script.onload = loadGoogleScript;
                script.onerror = (error) => {
                    console.error('Failed to load Google Sign-In script:', error);
                };
                document.head.appendChild(script);
            } else {
                loadGoogleScript();
            }
        }, 100);

        return () => {
            clearTimeout(initTimeout);
        };
    }, [handleGoogleSignIn, showRegister]);



    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const response = await fetchWithRetry(
                async () => fetch(API_ENDPOINTS.LOGIN, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password }),
                }),
                {
                    onRetry: ({ attempt, delay, maxRetries }) => {
                        setError(getTranslation('retryingRequest', uiLanguage, { seconds: Math.round(delay/1000) }));
                    }
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                // Save user session to cookies only if "Remember Me" is checked
                if (rememberMe && data.userId !== '1') {
                    saveUserSession(data.userId, username, data.sessionId);
                }
                // Store session token
                if (data.sessionId) {
                    localStorage.setItem('sessionId', data.sessionId);
                    localStorage.setItem('sessionExpiresAt', data.expiresAt);
                } else {
                    console.error('[Login] No sessionId in login response!');
                }
                // Pass the received userId to the onLoginSuccess callback
                onLoginSuccess(data.userId); 
            } else {
                // Check if it's an email verification error
                if (data.requiresVerification) {
                    setError('');
                    setUnverifiedEmail(data.email);
                } else {
                    setError(data.message || data.error || t('loginFailed'));
                }
            }
        } catch (err) {
            if (err instanceof RateLimitError) {
                setError(t('rateLimitExceeded'));
            } else {
                setError(t('connectionError'));
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle forgot password
    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotPasswordLoading(true);
        setForgotPasswordError('');
        setForgotPasswordSuccess(false);
        
        if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
            setForgotPasswordError(t('invalidEmail') || 'Please enter a valid email address');
            setForgotPasswordLoading(false);
            return;
        }
        
        try {
            const response = await fetch(API_ENDPOINTS.REQUEST_PASSWORD_RESET || '/api/request-password-reset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotPasswordEmail, language: uiLanguage }),
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                setForgotPasswordSuccess(true);
                setForgotPasswordError('');
                // Clear form after 5 seconds
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSuccess(false);
                    setForgotPasswordEmail('');
                }, 5000);
            } else if (response.status === 429) {
                setForgotPasswordError(data.error || 'Too many requests. Please try again later.');
            } else {
                setForgotPasswordError(data.error || 'Failed to send password reset email');
            }
        } catch (err) {
            setForgotPasswordError('Failed to send password reset email. Please try again.');
        } finally {
            setForgotPasswordLoading(false);
        }
    };
    
    // Handle resend verification email
    const handleResendVerification = async () => {
        setResendLoading(true);
        setError('');
        setResendSuccess(false);
        
        try {
            const response = await fetch(API_ENDPOINTS.RESEND_VERIFICATION || '/api/resend-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: unverifiedEmail, language: uiLanguage }),
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                setResendSuccess(true);
                setTimeout(() => setResendSuccess(false), 5000); // Hide success message after 5 seconds
            } else {
                setError(data.error || 'Failed to resend verification email');
            }
        } catch (err) {
            setError('Failed to resend verification email. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    // If password reset token is present, show reset password form
    if (resetToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
                <ResetPassword
                    token={resetToken}
                    onSuccess={(email) => {
                        // Clear URL and go back to login
                        window.history.replaceState({}, document.title, '/');
                        setResetToken(null);
                        // Force re-initialization of Google button after returning to login
                        setTimeout(() => {
                            const buttonContainer = document.getElementById('google-signin-button');
                            if (buttonContainer && window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
                                buttonContainer.innerHTML = '';
                                try {
                                    window.google.accounts.id.initialize({
                                        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                                        callback: handleGoogleSignIn,
                                        auto_select: false,
                                        cancel_on_tap_outside: true,
                                        use_fedcm_for_prompt: false,
                                    });
                                    window.google.accounts.id.renderButton(
                                        buttonContainer,
                                        { 
                                            theme: 'outline', 
                                            size: 'large',
                                            text: 'signin_with'
                                        }
                                    );
                                } catch (error) {
                                    console.error('Google Sign-In re-initialization error:', error);
                                }
                            }
                        }, 100);
                    }}
                    uiLanguage={uiLanguage}
                />
            </div>
        );
    }
    
    // If showing register form, render it instead
    if (showRegister) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
                <Register
                    onRegisterSuccess={onLoginSuccess}
                    onSwitchToLogin={() => {
                        setShowRegister(false);
                        // Force re-initialization of Google button when switching back
                        setTimeout(() => {
                            const buttonContainer = document.getElementById('google-signin-button');
                            if (buttonContainer && window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
                                buttonContainer.innerHTML = '';
                                try {
                                    window.google.accounts.id.initialize({
                                        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                                        callback: handleGoogleSignIn,
                                        auto_select: false,
                                        cancel_on_tap_outside: true,
                                        use_fedcm_for_prompt: false,
                                    });
                                    window.google.accounts.id.renderButton(
                                        buttonContainer,
                                        { 
                                            theme: 'outline', 
                                            size: 'large',
                                            text: 'signin_with'
                                        }
                                    );
                                } catch (error) {
                                    console.error('Google Sign-In re-initialization error:', error);
                                }
                            }
                        }, 100);
                    }}
                    uiLanguage={uiLanguage}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            <div className="max-w-md w-full">
                <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-8 pt-8 pb-6">
                        <div className="text-center mb-8">
                            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <h1 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, system-ui, -apple-system, sans-serif' }}>{t('loginTitle')}</h1>
                            <p className="text-lg text-gray-600 font-medium" style={{ letterSpacing: '0.01em' }}>{t('loginSubtitle')}</p>
                        </div>
                
                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center animate-shake">
                                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}
                        
                        {/* Unverified Email Message */}
                        {unverifiedEmail && (
                            <div className="mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                                <div className="flex items-start">
                                    <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
                                    </svg>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-semibold text-yellow-800 mb-1">
                                            {t('emailNotVerified') || 'Email Not Verified'}
                                        </h3>
                                        <p className="text-sm text-yellow-700 mb-3">
                                            {t('verifyEmailToLogin') || 'Please verify your email address to log in. Check your inbox for the verification email.'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleResendVerification}
                                            disabled={resendLoading || resendSuccess}
                                            className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {resendLoading ? (
                                                t('sendingEmail') || 'Sending...'
                                            ) : resendSuccess ? (
                                                t('emailSent') || 'Email Sent!'
                                            ) : (
                                                t('resendVerificationEmail') || 'Resend Verification Email'
                                            )}
                                        </button>
                                        {resendSuccess && (
                                            <p className="text-xs text-green-600 mt-2">
                                                {t('checkYourInbox') || 'Verification email sent! Check your inbox.'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Google Sign-In Button */}
                        <div className="space-y-4">
                            <div className="relative">
                                <div 
                                    id="google-signin-button"
                                    className="w-full flex justify-center transform transition-transform hover:scale-105"
                                    style={{ minHeight: '44px' }}
                                ></div>
                            </div>
                            
                            {isGoogleLoading && (
                                <div className="text-center">
                                    <div className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50">
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent mr-2"></div>
                                        <span className="text-sm font-medium text-blue-700">{t('signingIn')}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Show divider and additional options */}
                        <>
                                {/* Divider */}
                                <div className="relative my-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 bg-white text-gray-500 font-medium">{t('or')}</span>
                                    </div>
                                </div>

                                {/* Traditional Login Form */}
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="space-y-1">
                                        <label className="block text-sm font-semibold text-gray-700">{t('email') || t('username')}</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <input
                                                type={process.env.NODE_ENV === 'production' ? 'email' : 'text'}
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                                                placeholder={t('enterEmail') || t('enterUsername')}
                                                autoComplete={process.env.NODE_ENV === 'production' ? 'email' : 'username'}
                                                disabled={isLoading}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-1">
                                        <label className="block text-sm font-semibold text-gray-700">{t('password')}</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                                                placeholder={t('enterPassword')}
                                                autoComplete="off"
                                                disabled={isLoading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            >
                                                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {showPassword ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    )}
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="rememberMe"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                disabled={isLoading}
                                            />
                                            <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 cursor-pointer select-none">
                                                {t('rememberMe')}
                                            </label>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotPassword(true)}
                                            className="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition duration-150"
                                        >
                                            {t('forgotPassword') || 'Forgot password?'}
                                        </button>
                                    </div>
                                    
                                    <button 
                                        type="submit" 
                                        disabled={isLoading}
                                        className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-md transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                    >
                                        {isLoading ? (
                                            <span className="flex items-center justify-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('signingIn') || 'Signing in...'}
                                            </span>
                                        ) : (
                                            t('logIn')
                                        )}
                                    </button>
                                </form>

                                {/* Create Account Link */}
                                <div className="mt-6 text-center">
                                    <p className="text-sm text-gray-600">
                                        {t('noAccount') || "Don't have an account?"}{' '}
                                        <button
                                            onClick={() => setShowRegister(true)}
                                            className="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition duration-150"
                                        >
                                            {t('createAccount') || 'Create one'}
                                        </button>
                                    </p>
                                </div>
                            </>
                    </div>
                    
                    {/* Footer */}
                    <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
                        <p className="text-xs text-center text-gray-500">
                            {t('secureConnection')}
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {t('resetPassword') || 'Reset Password'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowForgotPassword(false);
                                    setForgotPasswordEmail('');
                                    setForgotPasswordError('');
                                    setForgotPasswordSuccess(false);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {!forgotPasswordSuccess ? (
                            <>
                                <p className="text-gray-600 mb-6">
                                    {t('resetPasswordDescription') || 'Enter your email address and we\'ll send you a link to reset your password.'}
                                </p>
                                
                                <form onSubmit={handleForgotPassword} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                            {t('email') || 'Email'}
                                        </label>
                                        <input
                                            type="email"
                                            value={forgotPasswordEmail}
                                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder={t('enterEmail') || 'Enter your email'}
                                            disabled={forgotPasswordLoading}
                                            autoFocus
                                        />
                                    </div>
                                    
                                    {forgotPasswordError && (
                                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                            <p className="text-sm text-red-600">{forgotPasswordError}</p>
                                        </div>
                                    )}
                                    
                                    <button
                                        type="submit"
                                        disabled={forgotPasswordLoading}
                                        className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {forgotPasswordLoading ? (
                                            <span className="flex items-center justify-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('sending') || 'Sending...'}
                                            </span>
                                        ) : (
                                            t('sendResetLink') || 'Send Reset Link'
                                        )}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center">
                                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('checkYourEmail') || 'Check Your Email'}
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    {t('resetLinkSent') || 'If an account exists with this email, a password reset link has been sent.'}
                                </p>
                                <button
                                    onClick={() => {
                                        setShowForgotPassword(false);
                                        setForgotPasswordSuccess(false);
                                        setForgotPasswordEmail('');
                                    }}
                                    className="text-blue-600 hover:text-blue-500 font-medium"
                                >
                                    {t('backToLogin') || 'Back to Login'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            <style>{`
                @keyframes shake {
                    0%, 100% {
                        transform: translateX(0);
                    }
                    10%, 30%, 50%, 70%, 90% {
                        transform: translateX(-2px);
                    }
                    20%, 40%, 60%, 80% {
                        transform: translateX(2px);
                    }
                }
                
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>
        </div>
    );
}
